import React, { useEffect, useState } from "react";
import { ParentingPlanSidebar } from "./ParentingPlanSidebar";
import { Heading } from "../../../design/heading";
import { ParentingPlanSection } from "../../../models/ParentingPlanSection";
import { ParentingPlanSectionComponent } from "./ParentingPlanSectionComponent";
import { ParentingPlanSectionUpdate } from "../types";

interface ParentingPlanEditorProps {
  sections: ParentingPlanSection[];
  onSectionsChange: (updatedSections: ParentingPlanSection[]) => void;
}

const LOCAL_STORAGE_KEY = "parentingPlanSelectedSection";

export const ParentingPlanEditor: React.FC<ParentingPlanEditorProps> = ({
  sections,
  onSectionsChange,
}) => {
  const [selectedSection, setSelectedSection] = useState<string | undefined>(
    sections.length > 0 ? sections[0]._id : undefined,
  );

  // Load from localStorage on mount
  useEffect(() => {
    const storedSectionId = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (
      storedSectionId &&
      sections.some((section) => section._id === storedSectionId)
    ) {
      setSelectedSection(storedSectionId);
    } else if (sections.length > 0 && !selectedSection) {
      setSelectedSection(sections[0]._id);
    }
  }, [sections]);

  // Save to localStorage on change
  useEffect(() => {
    if (selectedSection) {
      localStorage.setItem(LOCAL_STORAGE_KEY, selectedSection);
    }
  }, [selectedSection]);

  const updateSectionTree = (
    sections: ParentingPlanSection[],
    sectionIds: string[],
    changes: Partial<ParentingPlanSection>,
  ): ParentingPlanSection[] => {
    return sections.map((section) => {
      let updatedSection = { ...section };

      if (sectionIds.includes(section._id)) {
        updatedSection = { ...updatedSection, ...changes };
      }

      if (updatedSection.sections && updatedSection.sections.length > 0) {
        updatedSection.sections = updateSectionTree(
          updatedSection.sections,
          sectionIds,
          changes,
        );
      }

      return updatedSection;
    });
  };

  const handleSectionUpdate = ({
    sectionIds,
    changes,
  }: ParentingPlanSectionUpdate) => {
    const updatedSections = updateSectionTree(sections, sectionIds, changes);
    onSectionsChange(updatedSections);
  };

  const currentSection = sections?.find(
    (section) => section._id === selectedSection,
  );

  return (
    <div className="flex flex-col w-full h-full bg-gray-50">
      <div className="flex flex-grow overflow-hidden">
        {/* Sidebar */}
        <ParentingPlanSidebar
          sections={sections || []}
          selectedSection={selectedSection}
          onSelect={(sectionId) => setSelectedSection(sectionId)}
        />

        {/* Main Content */}
        <div className="flex-1 p-4 overflow-y-auto">
          <Heading>{currentSection?.title}</Heading>
          {currentSection && (
            <ParentingPlanSectionComponent
              section={currentSection}
              onSectionUpdate={handleSectionUpdate}
            />
          )}
        </div>
      </div>
    </div>
  );
};
